import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Card, Descriptions, Modal, Popconfirm, Select, Space, Tag, Typography, message } from 'antd';
import { CheckCircleOutlined, ExportOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { ingestionStore } from '../store/IngestionStore';
import { ingestionApi } from '../ingestionApi';
import { formatBytes, variationLabel } from '../ingestionHelpers';
import { openInEditor, VariantSelectorModal } from '../../common/openInEditor';
import { isBedType } from '../itemTypes';
import { useCurrentUser } from '../../../user/permissions';

const { Text } = Typography;

const SEND_BACK_STEPS = [
    { value: 2, label: 'Scrape' },
    { value: 3, label: 'Extract' },
    { value: 4, label: 'Assets' },
    { value: 5, label: 'Compress' },
];

export const Step6Confirm = observer(({ item }) => {
    const [summary, setSummary] = useState(null);
    const [busy, setBusy] = useState(null);
    const [backStep, setBackStep] = useState(4);
    const [selectorOpen, setSelectorOpen] = useState(false);
    const currentUserStore = useCurrentUser();
    const canMakeVisible = currentUserStore.canOnItem('upload-pipeline.make-visible', item);
    const canHide = currentUserStore.canOnItem('upload-pipeline.hide', item);

    const load = async () => {
        try {
            setSummary(await ingestionApi.getCompression(item.id));
        } catch (e) {
            message.error(e.message || 'Failed to load summary');
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item.id]);

    const published = Boolean(item.publishedCatalogItemId);
    const isLive = item.status === 'published';
    const variants = item.variants || [];

    // Color items carry no asset; every other type needs a compressed asset per
    // variant. A variant with 0 compressed bytes is missing its model/texture —
    // publishing would fail server-side, so block it and name the variants.
    const requiresAsset = item.itemType !== 'Color';
    const missingVariants =
        requiresAsset && summary
            ? variants.filter((v) => {
                  const c = (summary.variants || []).find((s) => s.variantId === v.id);
                  return !c || !c.compressedBytes;
              })
            : [];
    // For asset-requiring items, don't enable publish until the compression summary
    // has loaded — otherwise the missing-asset guard is fail-open (summary===null ⇒
    // missingVariants===[]) and the button is clickable before/without the check.
    const summaryReady = !requiresAsset || Boolean(summary);
    const canPublish = summaryReady && missingVariants.length === 0;

    // Containers are all-or-nothing across variants: if one model carries a
    // $$container_ plane, a sibling without one is almost certainly a mistake
    // (the editor treats the variants interchangeably). Warn, don't block —
    // hasContainer null means unknown, which never triggers the warning.
    const containerOf = (v) => (summary?.variants || []).find((s) => s.variantId === v.id)?.hasContainer;
    const noContainerVariants = variants.some((v) => containerOf(v) === true)
        ? variants.filter((v) => containerOf(v) === false)
        : [];

    // A bed without a mattress mesh publishes fine but its sheets/blanket textures
    // have nothing to land on in the editor — the mattress is added in the model
    // editor (step 4). Warn, don't block; null (unknown) never warns.
    const mattressOf = (v) => (summary?.variants || []).find((s) => s.variantId === v.id)?.hasMattress;
    const noMattressVariants = isBedType(item.itemType) ? variants.filter((v) => mattressOf(v) === false) : [];

    const confirmMattress = (proceed) => {
        if (!noMattressVariants.length) {
            proceed();
            return;
        }
        const labels = noMattressVariants.map((v) => variationLabel(item, v)).join(', ');
        Modal.confirm({
            title: 'Missing mattress',
            content: `${labels} variant${noMattressVariants.length > 1 ? "s have" : ' has'} no mattress mesh — sheets and blankets can't be applied to this bed. Add one in the model editor (Assets step), or publish anyway?`,
            okText: 'Publish anyway',
            onOk: proceed,
        });
    };

    const confirmContainers = (proceed) => {
        if (!noContainerVariants.length) {
            confirmMattress(proceed);
            return;
        }
        const labels = noContainerVariants.map((v) => variationLabel(item, v)).join(', ');
        Modal.confirm({
            title: 'Missing container plane',
            content: `${labels} variant${noContainerVariants.length > 1 ? "s don't" : " doesn't"} have a container, are you sure?`,
            okText: 'Publish anyway',
            onOk: () => confirmMattress(proceed),
        });
    };

    // Catalog variant id == pipeline variant id (the mapper reuses it). Open
    // directly for a single variant, else let the admin pick which to load.
    const openEditor = () => {
        if (variants.length === 1) {
            openInEditor(item.publishedCatalogItemId, variants[0].id);
        } else {
            setSelectorOpen(true);
        }
    };

    const run = async (key, fn, ok) => {
        setBusy(key);
        try {
            await fn();
            await ingestionStore.refreshCurrentItem(item.id);
            await load();
            if (ok) message.success(ok);
        } catch (e) {
            message.error(e.message || 'Action failed');
        } finally {
            setBusy(null);
        }
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card title="Summary">
                <Descriptions column={2} size="small">
                    <Descriptions.Item label="Name">{item.name}</Descriptions.Item>
                    <Descriptions.Item label="Type">{item.itemType}</Descriptions.Item>
                    <Descriptions.Item label="Variants">{(item.variants || []).length}</Descriptions.Item>
                    <Descriptions.Item label="Compressed size">
                        {summary ? formatBytes(summary.totalCompressedBytes) : '…'}
                    </Descriptions.Item>
                </Descriptions>
                {summary?.over3mb && (
                    <Alert
                        style={{ marginTop: 12 }}
                        type="warning"
                        showIcon
                        message="A variant's compressed asset exceeds 3 MB"
                        description="Each variant loads on its own, so the limit is per-variant. Large variants load slowly in the editor — consider a lighter model before publishing."
                    />
                )}
            </Card>

            <Card title="Publish">
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    {missingVariants.length > 0 && (
                        <Alert
                            type="error"
                            showIcon
                            message={`${missingVariants.length} variant(s) have no ${['WallPrint', 'Texture', 'Pillow', 'Curtain'].includes(item.itemType) ? 'texture' : 'model'}`}
                            description={
                                <>
                                    Generate or upload an asset in the Assets step before publishing:
                                    <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                                        {missingVariants.map((v) => (
                                            <li key={v.id}>{variationLabel(item, v)}</li>
                                        ))}
                                    </ul>
                                </>
                            }
                        />
                    )}
                    {!published ? (
                        <>
                            <Text type="secondary">
                                Publishes the item to the catalog with every variant hidden. You then review it and make
                                it visible.
                            </Text>
                            <Button
                                type="primary"
                                loading={busy === 'publish'}
                                disabled={!canPublish}
                                onClick={() =>
                                    confirmContainers(() =>
                                        run('publish', () => ingestionApi.publish(item.id), 'Published (hidden)'),
                                    )
                                }
                            >
                                Publish (hidden)
                            </Button>
                        </>
                    ) : (
                        <>
                            <Space wrap>
                                <Tag color={isLive ? 'success' : 'orange'} icon={isLive ? <CheckCircleOutlined /> : undefined}>
                                    {isLive ? 'Live in catalog' : 'Hidden in catalog'}
                                </Tag>
                                <Text type="secondary" copyable={{ text: item.publishedCatalogItemId }}>
                                    {item.publishedCatalogItemId}
                                </Text>
                                {currentUserStore.canOnItem('upload-pipeline.read', item) && (
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<ExportOutlined />}
                                        onClick={openEditor}
                                    >
                                        Open in editor
                                    </Button>
                                )}
                            </Space>
                            <Space wrap>
                                {!isLive && canMakeVisible && (
                                    <Button
                                        type="primary"
                                        loading={busy === 'visible'}
                                        onClick={() => run('visible', () => ingestionApi.makeVisible(item.id), 'Now visible in catalog')}
                                    >
                                        Make visible
                                    </Button>
                                )}
                                {isLive && canHide && (
                                    <Popconfirm
                                        title="Hide this item from the catalog?"
                                        description="App users stop seeing it in the catalog. It stays published and can be made visible again."
                                        okText="Hide"
                                        onConfirm={() => run('hide', () => ingestionApi.hide(item.id), 'Hidden from catalog')}
                                    >
                                        <Button icon={<EyeInvisibleOutlined />} loading={busy === 'hide'}>
                                            Hide from catalog
                                        </Button>
                                    </Popconfirm>
                                )}
                                <Button
                                    loading={busy === 'publish'}
                                    disabled={!canPublish}
                                    onClick={() => {
                                        const republish = () =>
                                            confirmContainers(() =>
                                                run('publish', () => ingestionApi.publish(item.id), 'Re-published'),
                                            );
                                        if (!isLive) {
                                            republish();
                                            return;
                                        }
                                        Modal.confirm({
                                            title: 'Re-publish a live item?',
                                            content:
                                                "You're about to re-publish an item that is already generally available. " +
                                                'Its catalog data and assets will be overwritten (it stays visible). Are you sure?',
                                            okText: 'Re-publish',
                                            onOk: republish,
                                        });
                                    }}
                                >
                                    Re-publish
                                </Button>
                            </Space>
                        </>
                    )}
                </Space>
            </Card>

            <Card title="Send back to a step" size="small">
                <Space>
                    <Select
                        value={backStep}
                        style={{ width: 160 }}
                        options={SEND_BACK_STEPS}
                        onChange={setBackStep}
                    />
                    <Button
                        loading={busy === 'back'}
                        onClick={() => run('back', () => ingestionApi.sendBack(item.id, backStep), 'Sent back')}
                    >
                        Send back
                    </Button>
                </Space>
            </Card>

            <VariantSelectorModal
                open={selectorOpen}
                itemId={item.publishedCatalogItemId}
                title={item.name}
                variants={variants.map((v) => ({ id: v.id, label: variationLabel(item, v) }))}
                onClose={() => setSelectorOpen(false)}
            />
        </Space>
    );
});
